// Expansion: the fixed part of investigating one anchor, done by the harness.
//
// Given an anchor and a plan (a preset per anchor kind, or a list of
// entities), the harness runs the pivots itself, feeds the graph, and only
// then hands the slice plus the fetched rows to a small stateless reader
// whose sole output is claims. The root spends one call instead of five,
// no query is improvised, and the leaf is the paper's `llm_query`: a slice
// in, structure out. This module is the pure part: plans, excerpts, and
// the reader's contract.

import { nodeKey } from "./investigationGraph.js";
import { needsTerm, pivotSpec, pivotsFrom } from "./graphPivot.js";

// What to fetch for an anchor kind when the root gives no plan. Term-bound
// steps run only when a term is supplied.
export const EXPAND_PRESETS = Object.freeze({
    session: [
        { to: "PROCESS_EVENT" },
        { to: "TRACE_TOOL_ACTIVITY" },
        { to: "CONVERSATION_TRACE" },
        { to: "CONVERSATION_SEARCH_CONTENT", needsTerm: true },
    ],
    endpoint: [
        { to: "ENDPOINT" },
        { to: "PROCESS_EVENT" },
        { to: "TELEMETRY_TIMELINE", needsTerm: true },
    ],
    process: [
        { to: "DNS_EVENT" },
        { to: "FILE_EVENT" },
        { to: "NETWORK_CONNECTION" },
        { to: "TRACE_TOOL_ACTIVITY" },
    ],
    person: [
        { to: "PROCESS_EVENT" },
    ],
});

export const MAX_STEPS = 6;
export const EXCERPT_ROWS = 16;
export const EXCERPT_BYTES = 20_000;

// Build the pivot specs for an anchor. Steps that cannot run (no term for
// a text view, no matching id field) are reported, not thrown, so one bad
// step never stops the others.
export function buildPlan({ schema, graph, anchor, plan = null, term = null, window = null, limit = null }) {
    const requested = Array.isArray(plan) && plan.length
        // A plan given as entity names keeps the text views' need for the
        // term, so a term supplied alongside it reaches them.
        ? plan.map((p) => (typeof p === "string" ? { to: p, needsTerm: needsTerm(p) } : p)).slice(0, MAX_STEPS)
        : (EXPAND_PRESETS[anchor.kind] || []).slice(0, MAX_STEPS);
    if (!requested.length) {
        const legal = pivotsFrom(schema, anchor).map((p) => p.entity);
        return { steps: [], skipped: [{ to: null, reason: `no preset for ${anchor.kind} anchors; pass plan: [${legal.slice(0, 4).join(", ")}]` }] };
    }
    const steps = [];
    const skipped = [];
    const cleanTerm = typeof term === "string" && term.trim() ? term.trim() : null;
    for (const step of requested) {
        if (step.needsTerm && !cleanTerm) { skipped.push({ to: step.to, reason: "needs a term" }); continue; }
        try {
            const built = pivotSpec({ schema, graph, anchor, to: step.to, term: step.term ?? (step.needsTerm || step.useTerm ? cleanTerm : null), window: step.window ?? window, limit: step.limit ?? limit });
            steps.push({ to: built.entity, idField: built.idField, spec: built.spec, notes: built.notes });
        } catch (err) {
            skipped.push({ to: step.to, reason: err?.message || String(err) });
        }
    }
    return { steps, skipped };
}

function clip(value, max = 160) {
    if (value == null) return null;
    const s = typeof value === "string" ? value : JSON.stringify(value);
    return s.length > max ? s.slice(0, max) + "..." : s;
}

// A bounded, readable excerpt of what the pivots fetched, for the reader.
function timeOf(row) {
    for (const [k, v] of Object.entries(row)) if (/time|_at$/i.test(k) && typeof v === "string" && v) return v;
    return null;
}

function chronological(rows) {
    return [...rows].sort((a, b) => String(timeOf(a) || "").localeCompare(String(timeOf(b) || "")));
}

// What to rank first in the excerpt: the term, plus any clock time the
// question names (23:44 in "who unlocked staging at 23:44 UTC").
export function needlesFor(term, question = null) {
    const out = [];
    if (term) out.push(String(term).toLowerCase());
    for (const m of String(question || "").matchAll(/\b(\d{1,2}):(\d{2})(?::\d{2})?\b/g)) out.push(`${m[1].padStart(2, "0")}:${m[2]}`);
    return [...new Set(out)];
}

// Rows the reader sees, in time order: every row when they fit, otherwise
// the ones that mention a needle first and then an even spread across the
// result including its first and last rows, so a long session's tail does
// not crowd out the moment the question is about.
export function pickRows(objects, rows, needles = []) {
    const all = Array.isArray(objects) ? objects : [];
    if (all.length <= rows) return { picked: chronological(all), hits: 0 };
    const hits = [];
    const rest = [];
    for (const row of all) {
        const hit = needles.length > 0 && Object.values(row).some((v) => typeof v === "string" && needles.some((n) => v.toLowerCase().includes(n)));
        (hit ? hits : rest).push(row);
    }
    const picked = hits.slice(0, rows);
    const need = rows - picked.length;
    if (need > 0 && rest.length) {
        if (need >= rest.length) picked.push(...rest);
        else {
            for (let i = 0; i < need; i++) {
                // one slot left: the middle row; otherwise an even spread from first to last
                const at = need === 1 ? Math.floor((rest.length - 1) / 2) : Math.round((i * (rest.length - 1)) / (need - 1));
                if (!picked.includes(rest[at])) picked.push(rest[at]);
            }
        }
    }
    return { picked: chronological(picked), hits: Math.min(hits.length, rows) };
}

export function rowExcerpt(stepResults, { rows = EXCERPT_ROWS, bytes = EXCERPT_BYTES, term = null, question = null } = {}) {
    const needles = needlesFor(term, question);
    const lines = [];
    let used = 0;
    for (const step of stepResults) {
        const { picked, hits } = pickRows(step.objects, rows, needles);
        let head = `## ${step.to}: ${step.error ? `error: ${clip(step.error, 200)}` : `${step.total} row${step.total === 1 ? "" : "s"}`}`;
        const times = (step.objects || []).map(timeOf).filter(Boolean).sort();
        if (times.length > 1) head += ` spanning ${times[0]} .. ${times[times.length - 1]} (UTC)`;
        if (picked.length) {
            head += step.total > picked.length
                ? ` (${picked.length} of ${step.total} shown in time order${hits ? `; ${hits} match ${needles.map((n) => `"${n}"`).join(" or ")}` : ", sampled across the result"})`
                : " (all shown, in time order)";
        }
        lines.push(head);
        used += head.length;
        for (const row of picked) {
            const compact = {};
            for (const [k, v] of Object.entries(row)) if (v != null && v !== "") compact[k] = clip(v);
            const line = JSON.stringify(compact);
            if (used + line.length > bytes) { lines.push("  ... excerpt budget reached"); used = bytes; break; }
            lines.push(line);
            used += line.length;
        }
        if (used >= bytes) break;
    }
    return lines.join("\n");
}

// Keys the reader may cite: the anchor, its neighbourhood, and every id
// the fetched rows carry.
export function citableKeys(graph, anchor, stepResults, { max = 40 } = {}) {
    const keys = new Set([anchor.key]);
    for (const step of stepResults) {
        for (const row of step.objects || []) {
            for (const [col, value] of Object.entries(row)) {
                if (value == null || value === "") continue;
                const tail = col.includes("__") ? col.slice(col.lastIndexOf("__") + 2) : col;
                let kind = null;
                if (/(^|_)trace_?id$|(^|_)session_?id$/i.test(tail)) kind = "session";
                else if (/(^|_)endpoint_?id$/i.test(tail)) kind = "endpoint";
                else if (/(^|_)process_start_key$/i.test(tail)) kind = "process";
                else if (/(^|_)message_?id$/i.test(tail)) kind = "message";
                if (kind) keys.add(nodeKey(kind, String(value)));
                if (keys.size >= max) return [...keys].filter((k) => graph.nodes.has(k));
            }
        }
    }
    return [...keys].filter((k) => graph.nodes.has(k));
}

export const READER_SYSTEM_PROMPT = [
    "You read the evidence an investigation harness fetched about one anchor and answer one question with claims.",
    "You get: the anchor, the graph slice around it (anchors carry evidence marks: E execution record, C conversation capture, P prompt summary, D directory), the rows the harness fetched, and the keys you may cite.",
    "Rules: a claim is one specific sentence (names, times, commands as they appear in the rows). Cite only keys from the list. An attribution (who ran X / on which machine / in which session) must rest on an execution row (a process event, telemetry, tool activity) or the model's own tool call in conversation content; if the rows do not contain one, say so in notes and do not make the attribution. Never infer beyond the rows. At most 4 claims.",
    "Each claim has a kind (attribution, activity, presence or finding) and, separately, a bearing. Bearing is \"answers\" when the claim bears on what the question asks (its time, action, actor or subject); \"context\" when it is true of this anchor but does not answer the question, such as activity outside the asked time or by someone else. When nothing in the rows answers the question, return context claims or none and say so in notes; never stretch a context claim into an answer.",
    "Return exactly { claims: [{ kind, statement, evidence: [keys], bearing, subject? }], notes } via return_result.",
].join("\n");

export const READER_OUTPUT_SCHEMA = {
    type: "object",
    properties: {
        claims: {
            type: "array",
            maxItems: 4,
            items: {
                type: "object",
                properties: {
                    kind: { type: "string", enum: ["attribution", "activity", "presence", "finding"] },
                    statement: { type: "string" },
                    evidence: { type: "array", items: { type: "string" } },
                    bearing: { type: "string", enum: ["answers", "context"] },
                    subject: { type: ["string", "null"] },
                },
                required: ["kind", "statement", "evidence", "bearing"],
            },
        },
        notes: { type: "string" },
    },
    required: ["claims"],
};

// A reader that writes the bearing word into `kind` still made a usable
// claim: recover it as a finding with that bearing instead of rejecting it.
const BEARINGS = new Set(["answers", "context"]);
export function normalizeReaderClaim(claim) {
    if (!claim || typeof claim !== "object") return claim;
    const kind = typeof claim.kind === "string" ? claim.kind.trim().toLowerCase() : claim.kind;
    if (BEARINGS.has(kind)) return { ...claim, kind: "finding", bearing: BEARINGS.has(claim.bearing) ? claim.bearing : kind };
    return kind === claim.kind ? claim : { ...claim, kind };
}

export function readerTask({ anchor, question, sliceText, excerpt, keys }) {
    return [
        `Anchor: ${anchor.key}${anchor.label ? ` (${anchor.label})` : ""}.`,
        `Question: ${question || "What did this anchor do, when, and on what evidence?"}`,
        "",
        "Graph slice:",
        sliceText || "(none)",
        "",
        "Fetched rows:",
        excerpt || "(none)",
        "",
        `Keys you may cite: ${keys.join(", ")}`,
    ].join("\n");
}
